import { useEffect, useRef, useState } from "react"
import { useIsHeaderPinnedToTop } from "../../../hooks/useIsHeaderPinnedToTop"
import { useNavigate, useParams } from "react-router-dom"
import { Button, IconButton } from "@chakra-ui/react"
import { MdAddCircleOutline, MdCheckCircle } from "react-icons/md"
import useStore from "../../../store"
import { useTranslation } from "react-i18next"
import { Widget, WidgetBody, WidgetHeader } from "../../ui/Widget"
import SearchBar from "../../common/SearchBar"
import UnitRow from "./UnitRow"
import CreateQuote from "../Quotes/CreateQuote"
import RequestTransportTile from "./RequestTransportTile"
import QuoteList from "./QuoteList"
import ScrollToTopButton from "../../common/ScrollToTopButton"
import unitFilter from "../../../utilities/unitFilter"
import { colors } from "../../../utilities/colors"
import useTransportationBasePath from "../../../hooks/useTransportationBasePath"

export default function CarpoolUnitsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const basePath = useTransportationBasePath()
  const { units, unitsFetching, dealer, fetchUnits, fetchUnitsAsAm } = useStore()
  const { dealerId } = useParams()

  const [selectedUnits, setSelectedUnits] = useState([])
  const [quoteOpen, setQuoteOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const unitsHeaderSentinelRef = useRef(null)
  const isUnitsHeaderPinnedToTop = useIsHeaderPinnedToTop(unitsHeaderSentinelRef)

  useEffect(() => {
    if (dealerId) {
      fetchUnitsAsAm(dealerId)
    } else {
      fetchUnits()
    }
  }, [dealerId])

  const validUnits = units?.filter(unit =>
    dealer?.attributes['line-ids']?.includes(Number(unit?.attributes['dealer-id']))
  ) || []

  const filteredUnits = unitFilter(validUnits, searchTerm)

  const handleToggle = (unit) => {
    setSelectedUnits((prev) =>
      prev.some((u) => u.id === unit.id)
        ? prev.filter((u) => u.id !== unit.id)
        : [...prev, unit]
    )
  }

  const handleRemove = (unit) => {
    setSelectedUnits((prev) => prev.filter((u) => u.id !== unit.id))
  }

  const handleAddVinVehicle = (shapedVehicle) => {
    const vin = shapedVehicle?.attributes?.vin?.toUpperCase()
    const unitToAdd = validUnits.find((u) => u.attributes?.vin?.toUpperCase() === vin) || shapedVehicle

    setSelectedUnits((prev) =>
      prev.some((u) => u.id === unitToAdd.id) ? prev : [...prev, unitToAdd]
    )
  }

  const handleQuoteClose = () => {
    setQuoteOpen(false)
    setSelectedUnits([])
  }

  const renderUnitList = () => {
    if (validUnits.length === 0) {
      return (
        <div className="text-center p-8 text-gray-600">
          {t('No units available for transport')}
        </div>
      )
    }

    if (filteredUnits.length === 0) {
      return (
        <div className="text-center p-4 text-gray-600">
          {t('No results found')}
        </div>
      )
    }

    return (
      <div>
        {filteredUnits.map((unit, index) => {
          const isSelected = selectedUnits.some((u) => u.id === unit.id)
          return (
            <UnitRow
              key={unit.id || index}
              unit={unit}
              filter={searchTerm}
              isSelected={isSelected}
              onClick={(u) => navigate(`${basePath}/${u.id}`)}
              trailing={
                <IconButton
                  aria-label={isSelected ? t('Remove from quote') : t('Add to quote')}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggle(unit)
                  }}
                  variant="ghost"
                  size="lg"
                  color={colors.green}
                  _hover={{ bg: `${colors.green}20` }}
                >
                  {isSelected ? <MdCheckCircle size={48} /> : <MdAddCircleOutline size={48} />}
                </IconButton>
              }
            />
          )
        })}
      </div>
    )
  }

  return (
    <>
      <Widget>
        <WidgetHeader
          title={t('Transport Vehicles')}
          showSpinner={unitsFetching}
        />
        <WidgetBody>
          <RequestTransportTile onAddVehicle={handleAddVinVehicle} />
          <QuoteList
            units={selectedUnits}
            onRemove={handleRemove}
            onGetQuote={() => setQuoteOpen(true)}
          />
        </WidgetBody>
      </Widget>

      <div ref={unitsHeaderSentinelRef} />
      <Widget>
        <WidgetHeader
          title={t('My Current Units')}
          actionIcon={
            selectedUnits.length > 0 && isUnitsHeaderPinnedToTop && (
              <Button
                onClick={() => setQuoteOpen(true)}
                variant="outline"
                bg="transparent"
                color={colors.green}
                borderColor={colors.green}
                borderWidth="1px"
                _hover={{ bg: colors.green, color: colors.grey100 }}
                h={{ base: '20px', lg: '22px' }}
                minH={{ base: '20px', lg: '22px' }}
                px={{ base: 2, lg: 3 }}
                borderRadius="full"
                fontSize={{ base: '2xs', lg: 'xs' }}
                fontWeight="semibold"
                lineHeight="1"
              >
                {`${t('Get Quote')} (${selectedUnits.length})`}
              </Button>
            )
          }
          overrideHeaderStyle={true}
        >
          <SearchBar
            onChange={setSearchTerm}
            value={searchTerm}
            handleReset={() => setSearchTerm('')}
          />
        </WidgetHeader>
        <WidgetBody>
          {renderUnitList()}
        </WidgetBody>
      </Widget>

      {quoteOpen && (
        <CreateQuote
          units={selectedUnits}
          isOpen={quoteOpen}
          onClose={handleQuoteClose}
        />
      )}

      <ScrollToTopButton />
    </>
  )
}
